import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { CommonConstants, IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { CustomerFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CustomerSelectionStepProps } from "models/propTypes/customerPropTypes";
//#endregion
//#region components
import { WizardSection, TextInput } from "components/common/wintForms";
import { Grid, Typography } from "@mui/material";
import { StyledButton, SubTitleWithIcon, TableAsync, useNavigationHistory } from "components/common";
import { useCustomerService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const CustomerSelectionWizardStep: React.FC<CustomerSelectionStepProps> = ({
  submitted,
  wizardProps,
  setValues,
  connect,
  customerName: customerNameFromProps,
}) => {
  const customerService = useCustomerService();

  const [searchText, setSearchText] = React.useState<string>("");
  const [results, setResults] = React.useState<WintSuperkollAPIModel.SearchCustomerInformationResponse[]>();
  const [loading, setLoading] = React.useState<boolean>(false);

  const { getRouteStateValue } = useNavigationHistory();

  React.useEffect(() => {
    const customerName = getRouteStateValue(InvoiceConstants.CustomerRoutePropName) ?? customerNameFromProps;
    if (customerName) {
      connect("generalInfo.name").setValue(customerName);
      setSearchText(customerName);
    }
  }, []);

  const onTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    connect("generalInfo.name").setValue(e.target.value);
  };

  const onSearchClick = () => {
    setLoading(true);
    customerService
      .searchExternalCustomers(searchText)
      .then((response) => {
        setResults(response ?? []);
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  };

  const onResultItemClick = (value: WintSuperkollAPIModel.SearchCustomerInformationResponse) => {
    const autogeneratedValues: Partial<CustomerFormTypes.GeneralInfo> = {
      name: value.name,
      orgNumber: value.orgNr?.replace("-", "").slice(0, CommonConstants.OrganizationNumberLength),
      billingAddress: {
        street1: value.street,
        street2: "",
        city: value.city,
        zipCode: value.zipCode,
        countryCode: InvoiceConstants.DefaultInvoiceCountry,
      },
    };

    setValues((prevValues) => ({
      ...prevValues,
      fromRegistry: autogeneratedValues,
      generalInfo: { ...prevValues.generalInfo, ...autogeneratedValues },
      alreadyExists: undefined,
    }));

    wizardProps.goToStep(wizardProps.currentStep + 1);
  };

  return (
    <WizardSection
      header={TranslationKeys.Invoice.CreateClient}
      description={TranslationKeys.Invoice.CreateClientDescription}
      submitted={submitted}
      wizardProps={wizardProps}
    >
      <Grid container direction="column">
        <Grid container item>
          <Grid item xs>
            <TextInput
              name="externalSearchInput"
              label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Name)}
              value={searchText}
              onChange={onTextChange}
              onBlur={() => {}}
              setTouched={() => {}}
              setValue={() => {}}
              touched={false}
              hasError={false}
            />
          </Grid>
          <Grid item className={cssExports.inputButton}>
            <StyledButton onClick={onSearchClick} disabled={!searchText} isSubmitting={loading}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SearchInregisters)}
            </StyledButton>
          </Grid>
        </Grid>
        {results !== undefined ? (
          results.length > 0 ? (
            <>
              <Grid container>
                <SubTitleWithIcon
                  subTitle={LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Invoice.CompanySearchResultText
                  )}
                  icon={IconDefinitionConstants.WBolt}
                ></SubTitleWithIcon>
              </Grid>

              <Grid container>
                <TableAsync
                  properties={{
                    name: {
                      description: TranslationKeys.Common.Name,
                      noWrap: true,
                    },
                    orgNr: {
                      description: TranslationKeys.Common.OrgNr,
                    },
                    street: {
                      description: TranslationKeys.Common.StreetAddress,
                    },
                    city: {
                      description: TranslationKeys.Common.City,
                    },
                    zipCode: {
                      description: TranslationKeys.Common.PostalCodeShort,
                    },
                    select: {
                      description: "",
                      formatter: (row: any) => (
                        <StyledButton size="xs" onClick={onResultItemClick.bind(null, row)}>
                          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Select)}
                        </StyledButton>
                      ),
                    },
                  }}
                  filter={results}
                  maxRowCount={10}
                  hidePaginationIfSinglePage
                  alternateColors
                />
              </Grid>
            </>
          ) : (
            <Grid container>
              <Typography className={cssExports.recentItemsText}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NotFound)}
              </Typography>
            </Grid>
          )
        ) : (
          <></>
        )}
      </Grid>
    </WizardSection>
  );
};

export default CustomerSelectionWizardStep;
